import type React from "react"
import type { PropertyTabKey } from "./property-tabs"
import type { ProjectTabKey } from "./sidebar-rail-model"

/**
 * Section icons shared by the mobile bottom bars and the desktop rail
 * (ZPL-352) — one source so a section looks the same wherever it appears.
 */

const HomeIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M2.5 7.5 8 2.5l5.5 5M4 6.5V13h8V6.5" />
	</svg>
)

const ProjectsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M3 3.5h10v9H3zM3 6.5h10M6 6.5V12.5" />
	</svg>
)

const JournalIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<circle cx="8" cy="8" r="5.5" />
		<path d="M8 5v3l2 1.5" />
	</svg>
)

const TicketsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M4 3.5h8v9H4z" />
		<path d="M6 6h4M6 8h4M6 10h2.5" />
	</svg>
)

const DocumentsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M9.5 2H4.5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5.5L9.5 2z" />
		<path d="M9.5 2v3.5H13M6 7h4M6 9h4M6 11h2" />
	</svg>
)

const BudgetIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<rect x="2" y="4.5" width="12" height="9" rx="1.5" />
		<path d="M2 7h12M10.5 10.5h1.5" />
	</svg>
)

const DetailsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<circle cx="8" cy="8" r="5.5" />
		<path d="M8 7.5v3M8 5.4v.2" />
	</svg>
)

const TasksIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M3 3.5h10v9H3z" />
		<path d="M5.5 8l1.8 1.8L10.5 6" />
	</svg>
)

const VisualizationsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M3 3.5h10v9H3z" />
		<circle cx="6" cy="6.5" r="1" />
		<path d="M3 11l3-3 2.5 2.5L11 8l2 2" />
	</svg>
)

const InspirationsIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<path d="M6 13h4M6.5 11h3" />
		<path d="M8 2.5a3.5 3.5 0 0 0-2 6.4c.5.4.8.9.8 1.4v.7h2.4v-.7c0-.5.3-1 .8-1.4a3.5 3.5 0 0 0-2-6.4z" />
	</svg>
)

const MembersIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-5 h-5"
		aria-hidden="true"
	>
		<circle cx="5.5" cy="6" r="2" />
		<circle cx="10.5" cy="6" r="2" />
		<path d="M2 13c.4-1.8 1.8-3 3.5-3s3.1 1.2 3.5 3M9.5 10.4c.3-.2.7-.4 1-.4 1.7 0 3.1 1.2 3.5 3" />
	</svg>
)

export const PROPERTY_TAB_ICONS: Record<PropertyTabKey, React.FC> = {
	overview: HomeIcon,
	projects: ProjectsIcon,
	tickets: TicketsIcon,
	journal: JournalIcon,
	documents: DocumentsIcon,
	budget: BudgetIcon,
}

export const PROJECT_TAB_ICONS: Record<ProjectTabKey, React.FC> = {
	details: DetailsIcon,
	tasks: TasksIcon,
	visualizations: VisualizationsIcon,
	inspirations: InspirationsIcon,
	documents: DocumentsIcon,
	budget: BudgetIcon,
	members: MembersIcon,
}
